import * as React from "react"

// ── Slot symbol keys ────────────────────────────────────────────────────
export type SlotSymbol =
  | "coin"
  | "cherry"
  | "middleFinger"
  | "lemon"
  | "watermelon"
  | "bell"
  | "seven"
  | "diamond"
  | "star"
  | "gift"
  | "bill"
  | "refresh"
  | "swords"

// ── Individual icon components ──────────────────────────────────────────
// All icons use a 48×48 viewBox. Solid fills with layered opacity for depth.
// Colors are chosen to pop on the dark (#0A0A18) reel backgrounds.

function CoinIcon({ size }: { size: number }) {
  const id = React.useId()
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <radialGradient id={`${id}-bg`} cx="40%" cy="35%">
          <stop offset="0%" stopColor="#FFE566" />
          <stop offset="100%" stopColor="#CC9900" />
        </radialGradient>
      </defs>
      <circle cx="24" cy="24" r="21" fill={`url(#${id}-bg)`} />
      <circle cx="24" cy="24" r="21" fill="white" opacity="0.12" />
      <circle cx="24" cy="24" r="17" fill="none" stroke="#B8860B" strokeWidth="1.5" opacity="0.5" />
      <text
        x="24" y="31"
        textAnchor="middle"
        fontSize="22"
        fontWeight="900"
        fontFamily="Bangers, Arial Black, sans-serif"
        fill="#7A5C00"
      >
        $
      </text>
      <text
        x="24" y="30.5"
        textAnchor="middle"
        fontSize="22"
        fontWeight="900"
        fontFamily="Bangers, Arial Black, sans-serif"
        fill="#FFF8DC"
        opacity="0.85"
      >
        $
      </text>
      {/* Highlight arc */}
      <ellipse cx="20" cy="14" rx="9" ry="4" fill="white" opacity="0.18" />
    </svg>
  )
}

function CherryIcon({ size }: { size: number }) {
  const id = React.useId()
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <radialGradient id={`${id}-l`} cx="35%" cy="35%">
          <stop offset="0%" stopColor="#FF6B6B" />
          <stop offset="100%" stopColor="#CC0000" />
        </radialGradient>
        <radialGradient id={`${id}-r`} cx="35%" cy="35%">
          <stop offset="0%" stopColor="#FF5252" />
          <stop offset="100%" stopColor="#B30000" />
        </radialGradient>
      </defs>
      {/* Stem */}
      <path d="M24 6 C24 6 20 14 15 22" stroke="#2E7D32" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <path d="M24 6 C24 6 28 14 33 22" stroke="#2E7D32" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      {/* Leaf */}
      <path d="M24 6 Q30 4 32 8 Q28 10 24 6Z" fill="#4CAF50" />
      <path d="M24 6 Q30 4 32 8" stroke="#388E3C" strokeWidth="0.8" fill="none" />
      {/* Left cherry */}
      <circle cx="15" cy="32" r="10" fill={`url(#${id}-l)`} />
      <ellipse cx="12" cy="28" rx="3" ry="2" fill="white" opacity="0.25" />
      {/* Right cherry */}
      <circle cx="33" cy="32" r="10" fill={`url(#${id}-r)`} />
      <ellipse cx="30" cy="28" rx="3" ry="2" fill="white" opacity="0.25" />
    </svg>
  )
}

function MiddleFingerIcon({ size }: { size: number }) {
  const id = React.useId()
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <linearGradient id={`${id}-g`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FF6B6B" />
          <stop offset="100%" stopColor="#CC2233" />
        </linearGradient>
      </defs>
      {/* Middle finger */}
      <rect x="19" y="3" width="10" height="26" rx="5" fill={`url(#${id}-g)`} />
      {/* Fingernail */}
      <rect x="20.5" y="4" width="7" height="7" rx="3.5" fill="#FF9999" opacity="0.5" />
      {/* Fist base */}
      <rect x="10" y="27" width="28" height="16" rx="6" fill={`url(#${id}-g)`} />
      {/* Knuckle lines */}
      <line x1="17" y1="29" x2="17" y2="34" stroke="#AA1122" strokeWidth="1.2" strokeLinecap="round" opacity="0.5" />
      <line x1="31" y1="29" x2="31" y2="34" stroke="#AA1122" strokeWidth="1.2" strokeLinecap="round" opacity="0.5" />
      {/* Thumb */}
      <ellipse cx="10" cy="33" rx="4" ry="5" fill="#E85555" />
      {/* Highlight */}
      <rect x="21" y="8" width="3" height="12" rx="1.5" fill="white" opacity="0.12" />
    </svg>
  )
}

function LemonIcon({ size }: { size: number }) {
  const id = React.useId()
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <radialGradient id={`${id}-g`} cx="40%" cy="35%">
          <stop offset="0%" stopColor="#FFF176" />
          <stop offset="100%" stopColor="#F9A825" />
        </radialGradient>
      </defs>
      <ellipse cx="24" cy="26" rx="16" ry="14" fill={`url(#${id}-g)`} />
      {/* Tip */}
      <path d="M8 26 Q4 20 10 16" stroke="#F9A825" strokeWidth="2" fill="#FDD835" />
      {/* Highlight */}
      <ellipse cx="20" cy="20" rx="7" ry="4" fill="white" opacity="0.2" transform="rotate(-15 20 20)" />
      {/* Leaf */}
      <path d="M34 14 Q38 8 42 10 Q40 16 34 14Z" fill="#66BB6A" />
    </svg>
  )
}

function WatermelonIcon({ size }: { size: number }) {
  const id = React.useId()
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <linearGradient id={`${id}-r`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FF5252" />
          <stop offset="100%" stopColor="#D32F2F" />
        </linearGradient>
      </defs>
      {/* Rind */}
      <path d="M6 30 A20 20 0 0 1 42 30 Z" fill="#388E3C" />
      <path d="M8 30 A18 18 0 0 1 40 30 Z" fill="#A5D6A7" />
      {/* Flesh */}
      <path d="M10 30 A16 16 0 0 1 38 30 Z" fill={`url(#${id}-r)`} />
      {/* Seeds */}
      <ellipse cx="17" cy="25" rx="1.5" ry="2.5" fill="#3E2723" transform="rotate(-10 17 25)" />
      <ellipse cx="24" cy="23" rx="1.5" ry="2.5" fill="#3E2723" />
      <ellipse cx="31" cy="25" rx="1.5" ry="2.5" fill="#3E2723" transform="rotate(10 31 25)" />
      <ellipse cx="20" cy="28" rx="1.2" ry="2" fill="#3E2723" transform="rotate(-5 20 28)" />
      <ellipse cx="28" cy="28" rx="1.2" ry="2" fill="#3E2723" transform="rotate(5 28 28)" />
      {/* Highlight */}
      <path d="M14 28 A12 12 0 0 1 34 28" fill="white" opacity="0.08" />
    </svg>
  )
}

function BellIcon({ size }: { size: number }) {
  const id = React.useId()
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <linearGradient id={`${id}-g`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFD54F" />
          <stop offset="100%" stopColor="#F57F17" />
        </linearGradient>
      </defs>
      {/* Bell body */}
      <path
        d="M24 6 C24 6 12 10 10 26 L10 32 Q10 36 14 36 L34 36 Q38 36 38 32 L38 26 C36 10 24 6 24 6Z"
        fill={`url(#${id}-g)`}
      />
      {/* Inner shadow */}
      <path d="M14 28 Q14 34 18 36 L30 36 Q34 34 34 28" fill="#E65100" opacity="0.2" />
      {/* Highlight */}
      <path d="M18 12 Q22 10 24 12 Q20 18 16 20" fill="white" opacity="0.2" />
      {/* Clapper */}
      <circle cx="24" cy="39" r="3.5" fill="#BF360C" />
      <circle cx="24" cy="39" r="3.5" fill="white" opacity="0.1" />
      {/* Top nub */}
      <circle cx="24" cy="6" r="2.5" fill="#F9A825" />
    </svg>
  )
}

function SevenIcon({ size }: { size: number }) {
  const id = React.useId()
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <linearGradient id={`${id}-g`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FF5252" />
          <stop offset="100%" stopColor="#B71C1C" />
        </linearGradient>
      </defs>
      {/* Bold 7 shape */}
      <path
        d="M12 6 L36 6 L36 14 L26 44 L17 44 L26 16 L12 16 Z"
        fill={`url(#${id}-g)`}
      />
      {/* Inline highlight */}
      <path
        d="M14 8 L34 8 L34 13 L25 13 L14 13 Z"
        fill="white"
        opacity="0.2"
      />
      {/* Outline */}
      <path
        d="M12 6 L36 6 L36 14 L26 44 L17 44 L26 16 L12 16 Z"
        stroke="#7F0000"
        strokeWidth="1"
        fill="none"
        opacity="0.3"
      />
    </svg>
  )
}

function DiamondIcon({ size }: { size: number }) {
  const id = React.useId()
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <linearGradient id={`${id}-g`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#80DEEA" />
          <stop offset="50%" stopColor="#00BCD4" />
          <stop offset="100%" stopColor="#006064" />
        </linearGradient>
      </defs>
      {/* Top facets */}
      <polygon points="24,2 38,16 24,16" fill="#4DD0E1" />
      <polygon points="24,2 10,16 24,16" fill="#00ACC1" />
      {/* Bottom facets */}
      <polygon points="10,16 24,46 24,16" fill="#00838F" />
      <polygon points="38,16 24,46 24,16" fill={`url(#${id}-g)`} />
      {/* Crown line */}
      <line x1="10" y1="16" x2="38" y2="16" stroke="white" strokeWidth="0.8" opacity="0.3" />
      {/* Sparkle highlight */}
      <polygon points="18,10 20,8 22,10 20,12" fill="white" opacity="0.5" />
    </svg>
  )
}

function StarIcon({ size }: { size: number }) {
  const id = React.useId()
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <radialGradient id={`${id}-g`} cx="45%" cy="40%">
          <stop offset="0%" stopColor="#FFE082" />
          <stop offset="100%" stopColor="#FF8F00" />
        </radialGradient>
      </defs>
      <polygon
        points="24,2 30,17 46,17 33,27 38,43 24,33 10,43 15,27 2,17 18,17"
        fill={`url(#${id}-g)`}
      />
      {/* Upper highlight */}
      <polygon
        points="24,6 28,17 38,17 30,24"
        fill="white"
        opacity="0.18"
      />
      {/* Outline */}
      <polygon
        points="24,2 30,17 46,17 33,27 38,43 24,33 10,43 15,27 2,17 18,17"
        stroke="#E65100"
        strokeWidth="0.8"
        fill="none"
        opacity="0.3"
      />
    </svg>
  )
}

function GiftIcon({ size }: { size: number }) {
  const id = React.useId()
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <linearGradient id={`${id}-g`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FF7043" />
          <stop offset="100%" stopColor="#D84315" />
        </linearGradient>
      </defs>
      {/* Box bottom */}
      <rect x="8" y="22" width="32" height="22" rx="3" fill={`url(#${id}-g)`} />
      {/* Lid */}
      <rect x="6" y="16" width="36" height="8" rx="3" fill="#FF8A65" />
      {/* Vertical ribbon */}
      <rect x="21" y="16" width="6" height="28" fill="#FFD54F" />
      {/* Horizontal ribbon */}
      <rect x="6" y="18" width="36" height="4" fill="#FFD54F" />
      {/* Bow loops */}
      <ellipse cx="19" cy="14" rx="6" ry="5" fill="#FFD54F" />
      <ellipse cx="29" cy="14" rx="6" ry="5" fill="#FFD54F" />
      <ellipse cx="19" cy="14" rx="4" ry="3" fill="#FF8A65" />
      <ellipse cx="29" cy="14" rx="4" ry="3" fill="#FF8A65" />
      {/* Bow center */}
      <circle cx="24" cy="15" r="3" fill="#FFD54F" />
      {/* Highlight */}
      <rect x="10" y="24" width="10" height="3" rx="1.5" fill="white" opacity="0.12" />
    </svg>
  )
}

function BillIcon({ size }: { size: number }) {
  const id = React.useId()
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <linearGradient id={`${id}-g`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#66BB6A" />
          <stop offset="100%" stopColor="#2E7D32" />
        </linearGradient>
      </defs>
      {/* Bill shape */}
      <rect x="4" y="12" width="40" height="24" rx="3" fill={`url(#${id}-g)`} />
      {/* Border inset */}
      <rect x="7" y="15" width="34" height="18" rx="2" fill="none" stroke="#1B5E20" strokeWidth="1" opacity="0.4" />
      {/* Center circle */}
      <circle cx="24" cy="24" r="8" fill="none" stroke="#A5D6A7" strokeWidth="1.2" opacity="0.5" />
      {/* Dollar sign */}
      <text
        x="24" y="29"
        textAnchor="middle"
        fontSize="16"
        fontWeight="900"
        fontFamily="Bangers, Arial Black, sans-serif"
        fill="#E8F5E9"
        opacity="0.9"
      >
        $
      </text>
      {/* Corner details */}
      <circle cx="10" cy="18" r="2" fill="#A5D6A7" opacity="0.3" />
      <circle cx="38" cy="18" r="2" fill="#A5D6A7" opacity="0.3" />
      <circle cx="10" cy="30" r="2" fill="#A5D6A7" opacity="0.3" />
      <circle cx="38" cy="30" r="2" fill="#A5D6A7" opacity="0.3" />
      {/* Highlight */}
      <rect x="6" y="13" width="20" height="4" rx="2" fill="white" opacity="0.1" />
    </svg>
  )
}

function RefreshIcon({ size }: { size: number }) {
  const id = React.useId()
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <linearGradient id={`${id}-g`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#CE93D8" />
          <stop offset="100%" stopColor="#7B1FA2" />
        </linearGradient>
      </defs>
      {/* Top arrow arc */}
      <path
        d="M36 16 A14 14 0 0 0 12 16"
        stroke={`url(#${id}-g)`}
        strokeWidth="5"
        strokeLinecap="round"
        fill="none"
      />
      {/* Top arrowhead */}
      <polygon points="36,10 42,17 34,20" fill="#CE93D8" />
      {/* Bottom arrow arc */}
      <path
        d="M12 32 A14 14 0 0 0 36 32"
        stroke={`url(#${id}-g)`}
        strokeWidth="5"
        strokeLinecap="round"
        fill="none"
      />
      {/* Bottom arrowhead */}
      <polygon points="12,38 6,31 14,28" fill="#AB47BC" />
      {/* Center glow */}
      <circle cx="24" cy="24" r="5" fill="#CE93D8" opacity="0.15" />
    </svg>
  )
}

function SwordsIcon({ size }: { size: number }) {
  const id = React.useId()
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <linearGradient id={`${id}-b`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#E0E0E0" />
          <stop offset="100%" stopColor="#78909C" />
        </linearGradient>
      </defs>
      {/* Left sword blade */}
      <path d="M8 4 L22 26 L18 28 L4 8Z" fill={`url(#${id}-b)`} />
      {/* Left guard */}
      <rect x="13" y="26" width="14" height="3" rx="1.5" fill="#FFD54F" transform="rotate(-25 20 27.5)" />
      {/* Left handle */}
      <rect x="8" y="30" width="4" height="10" rx="2" fill="#8D6E63" transform="rotate(-25 10 35)" />
      <circle cx="10" cy="42" r="2.5" fill="#FFD54F" transform="rotate(-25 10 42)" />
      {/* Right sword blade */}
      <path d="M40 4 L26 26 L30 28 L44 8Z" fill={`url(#${id}-b)`} />
      {/* Right guard */}
      <rect x="21" y="26" width="14" height="3" rx="1.5" fill="#FFD54F" transform="rotate(25 28 27.5)" />
      {/* Right handle */}
      <rect x="36" y="30" width="4" height="10" rx="2" fill="#8D6E63" transform="rotate(25 38 35)" />
      <circle cx="38" cy="42" r="2.5" fill="#FFD54F" transform="rotate(25 38 42)" />
      {/* Clash spark */}
      <polygon points="24,18 26,14 28,18 26,22" fill="white" opacity="0.6" />
    </svg>
  )
}

// ── Lookup & public component ───────────────────────────────────────────

const ICON_COMPONENTS: Record<SlotSymbol, React.FC<{ size: number }>> = {
  coin: CoinIcon,
  cherry: CherryIcon,
  middleFinger: MiddleFingerIcon,
  lemon: LemonIcon,
  watermelon: WatermelonIcon,
  bell: BellIcon,
  seven: SevenIcon,
  diamond: DiamondIcon,
  star: StarIcon,
  gift: GiftIcon,
  bill: BillIcon,
  refresh: RefreshIcon,
  swords: SwordsIcon,
}

export function SlotIcon({ symbol, size = 48 }: { symbol: string; size?: number }) {
  const Comp = ICON_COMPONENTS[symbol as SlotSymbol]
  if (!Comp) return <span style={{ fontSize: size * 0.8 }}>{symbol}</span>
  return <Comp size={size} />
}
